import { Helmet } from "react-helmet";
import { Text, getDateRangeByValue } from "@pushpress/shared-components";
import Dashboard from "../../../layouts/Dashboard";
import CelebrationsTable from "./CelebrationsTable";
import { Route, Switch, useHistory } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import {
  CelebrationItem,
  CelebrationType,
  convertToCelebrationAnniversary,
  convertToCelebrationBirthday,
} from "./Celebrations.utils";
import { DateRangeValue } from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";
import { AppContext } from "../../../context/app";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import {
  useAnniversariesQuery,
  useBirthdaysQuery,
} from "../../../apis/profileApi.hooks";

function Celebrations() {
  return (
    <Dashboard>
      <Helmet>
        <title>Celebrations</title>
      </Helmet>
      <Switch>
        <Route path="/reports/celebrations" exact>
          <CelebrationsContent celebrationType="all" />
        </Route>
        <Route path="/reports/celebrations/birthday">
          <CelebrationsContent celebrationType="birthday" />
        </Route>
        <Route path="/reports/celebrations/anniversary">
          <CelebrationsContent celebrationType="anniversary" />
        </Route>
      </Switch>
    </Dashboard>
  );
}

interface CelebrationsContentProps {
  celebrationType: CelebrationType;
}

export function CelebrationsContent(props: CelebrationsContentProps) {
  const { celebrationType } = props;

  const history = useHistory();
  const { client } = useContext(AppContext);

  const [dateRange, setDateRange] = useState<DateRangeValue>("next-30-days");
  const dateRangeValue = getDateRangeByValue(dateRange, client.timezone);
  const start = dateRangeValue.start.split("T")[0];
  const end = dateRangeValue.end.split("T")[0];

  const { data: { data: birthdays = [] } = {}, isLoading: isBirthdaysLoading } =
    useBirthdaysQuery(
      {
        from: start,
        to: end,
      },
      {
        onError: (error) => {
          toast.error(extractError(error), defaultToastOptions);
        },
      },
    );

  const {
    data: { data: anniversaries = [] } = {},
    isLoading: isAnniversariesLoading,
  } = useAnniversariesQuery(
    {
      from: start,
      to: end,
    },
    {
      onError: (error) => {
        toast.error(extractError(error), defaultToastOptions);
      },
    },
  );

  const celebrations = useMemo<CelebrationItem[]>(() => {
    const result: CelebrationItem[] = [];
    if (celebrationType === "all" || celebrationType === "birthday") {
      result.push(...birthdays.map(convertToCelebrationBirthday));
    }
    if (celebrationType === "all" || celebrationType === "anniversary") {
      result.push(...anniversaries.map(convertToCelebrationAnniversary));
    }
    return result;
  }, [birthdays, anniversaries, celebrationType]);

  const handleCelebrationTypeChange = (type: CelebrationType) => {
    const path = type === "all" ? "" : `/${type}`;
    history.push(`/reports/celebrations${path}`);
  };

  const birthdayCount = birthdays.length;
  const anniversaryCount = anniversaries.length;
  const allCount = birthdayCount + anniversaryCount;

  return (
    <div>
      <Text variant="heading-3" color="wolf-800">
        Celebrations
      </Text>
      <div className="mt-4">
        <CelebrationsTable
          dateRange={dateRange}
          data={celebrations}
          loading={isBirthdaysLoading || isAnniversariesLoading}
          allCount={allCount}
          birthdayCount={birthdayCount}
          anniversaryCount={anniversaryCount}
          celebrationType={celebrationType}
          onCelebrationTypeChange={handleCelebrationTypeChange}
          onDateRangeChange={setDateRange}
        />
      </div>
    </div>
  );
}

export default Celebrations;
